<template>
  <Teleport to="body">
    <Transition name="overlay-fade">
      <div
        v-if="visible"
        :style="{ top: `${topOffset}px` }"
        class="fixed left-1/2 -translate-x-1/2 z-[9999] w-[90%] max-w-[360px]"
      >
        <div class="bg-content rounded-xl shadow-[0px_3px_10px_0px_#00000033] overflow-hidden">
          <!-- 主体内容 -->
          <div class="flex items-center justify-between px-4 py-3">
            <!-- 左侧：金额 + KB -->
            <div class="flex items-baseline gap-1">
              <span class="text-[22px] font-din-bold text-primary leading-7">{{
                orderData.amount
              }}</span>
              <span class="text-xs text-text-primary">{{ currency }}</span>
            </div>
            <i class="shrink-0 border-l border-border-dark not-italic h-5 ml-2" />
            <!-- 中间：状态文字 -->
            <div class="flex-1 px-2 text-xs font-semibold text-text-primary">
              {{ title }}
            </div>

            <!-- 右侧：按钮组 -->
            <div class="flex gap-2">
              <van-button type="primary" size="small" plain class="h-7!" @click.stop="handleIgnore">
                忽略
              </van-button>
              <van-button type="primary" size="small" class="h-7!" @click.stop="handleView">
                查看
              </van-button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { OrderNotifyKind, NotifyAckKind } from '@/enums/chat'
import { useNotifyAck } from '@/composables/useNotifyAck'
import { useAssetsStore } from '@/stores/assets'
import { useNotifyStore } from '@/stores/notify'

const notifyStore = useNotifyStore()

interface OrderNotifyData {
  kind: OrderNotifyKind | string
  msg_id: number
  sent_at: number
  feed: {
    order_no: string
    amount: string
    expires_at: number
    server_now: number
  }
}

interface Props {
  data: OrderNotifyData
  topOffset?: number
}

const props = withDefaults(defineProps<Props>(), {
  topOffset: 40,
})
const emit = defineEmits<{
  close: []
  ignore: []
}>()

const router = useRouter()
const { ackNotifyWithParams } = useNotifyAck()
const visible = ref(false)
const expiresIn = ref(0)
let countdownTimer: number | null = null
const assetsStore = useAssetsStore()
const currency = computed(() => assetsStore.currency)

const title = computed(() => {
  switch (props.data.kind) {
    case OrderNotifyKind.OrderToConfirm:
      return '等待您确认'
    case OrderNotifyKind.OrderToTransfer:
      return '等待买家转账'
    case OrderNotifyKind.OrderToDeliver:
      return '等待您发货'
    default:
      return '等待处理'
  }
})

const orderData = computed(() => props.data.feed)

function updateCountdown() {
  const now = Math.floor(Date.now() / 1000)
  const remaining = Math.floor(props.data.feed.expires_at / 1000) - now
  expiresIn.value = Math.max(0, remaining)

  if (expiresIn.value <= 0 && countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function stopCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

async function readHandle() {
  await ackNotifyWithParams({
    kind: NotifyAckKind.Overlay,
    msg_id: props.data.msg_id,
  })

  setTimeout(() => {
    notifyStore.removeOrderNotification(props.data.msg_id)
  }, 300)
}

async function handleIgnore() {
  await readHandle()

  visible.value = false
  emit('ignore')
  setTimeout(() => {
    emit('close')
  }, 300)
}

async function handleView() {
  await readHandle()
  visible.value = false
  router.push(`/order/${props.data.feed.order_no}`)
}

onMounted(() => {
  visible.value = true
})

onUnmounted(() => {
  stopCountdown()
})
</script>

<style scoped>
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: all 0.3s ease;
}

.overlay-fade-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.overlay-fade-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
